import React, { useEffect, useRef, useState } from 'react';
import { Loader2, Search, Trash } from 'lucide-react';

/**
 * Seção compacta "Cliente e grupo" (campos + integrantes + busca dropdown).
 */
export default function ClienteGrupoSection({
  showGrupoNome = false,
  grupoNome = '',
  onGrupoNomeChange,
  grupoNomeId = 'uc-cg-nome-grupo',
  nomePortal = '',
  onNomePortalChange,
  nomePortalId = 'uc-cg-nome-portal',
  showNomePortal = true,
  nomePortalMax = 30,
  integrantes = [],
  onRemoveIntegrante,
  removingCodigo = null,
  pendingIntegrantes = [],
  onRemovePending,
  query = '',
  onQueryChange,
  searchResults = [],
  searchLoading = false,
  searchMin = 2,
  selectedIds,
  onToggleSelect,
  searchDisabled = false,
  searchAriaLabel = 'Buscar clientes',
}) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const wrapRef = useRef(null);
  const q = String(query || '').trim();
  const showDropdown = dropdownOpen && !searchDisabled;
  const tooShort = q.length > 0 && q.length < searchMin;
  const noResults =
    q.length >= searchMin && !searchLoading && Array.isArray(searchResults) && searchResults.length === 0;

  const totalCount = (integrantes?.length || 0) + (pendingIntegrantes?.length || 0);
  const countLabel = totalCount === 1 ? '1 cliente' : `${totalCount} clientes`;

  useEffect(() => {
    if (!dropdownOpen) return undefined;
    const onPointerDown = (e) => {
      if (wrapRef.current?.contains(e.target)) return;
      setDropdownOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, [dropdownOpen]);

  const isSelected = (id) => (selectedIds ? selectedIds.has(id) : false);

  return (
    <div className="uc-cg-section">
      {(showGrupoNome || showNomePortal) && (
        <div className={`uc-cg-fields${showGrupoNome && showNomePortal ? ' uc-cg-fields--2' : ''}`}>
          {showGrupoNome && (
            <div className="uc-cg-field">
              <label className="uc-cg-label" htmlFor={grupoNomeId}>
                Nome do grupo
              </label>
              <input
                id={grupoNomeId}
                className="uc-cg-input"
                value={grupoNome}
                onChange={(e) => onGrupoNomeChange?.(e.target.value)}
                placeholder="Ex.: Clínicas 2"
                autoComplete="off"
              />
            </div>
          )}
          {showNomePortal && (
            <div className="uc-cg-field">
              <label className="uc-cg-label" htmlFor={nomePortalId}>
                Nome no portal
              </label>
              <input
                id={nomePortalId}
                className="uc-cg-input"
                value={nomePortal}
                onChange={(e) => onNomePortalChange?.(e.target.value.slice(0, nomePortalMax))}
                placeholder={`Até ${nomePortalMax} caracteres (opcional)`}
                maxLength={nomePortalMax}
                autoComplete="off"
              />
            </div>
          )}
        </div>
      )}

      <div className="uc-cg-block">
        <div className="uc-cg-block-head">
          <span className="uc-cg-label">Integrantes</span>
          <span className="uc-cg-count-badge">{countLabel}</span>
        </div>

        <div className="uc-cg-list" role="region" aria-label="Integrantes do grupo">
          <div className="uc-cg-list-head" aria-hidden>
            <span>Código</span>
            <span>Razão social</span>
            <span className="uc-cg-col-ctr">Contratos</span>
            <span />
          </div>
          <div className="uc-cg-list-body">
            {totalCount === 0 ? (
              <div className="uc-cg-empty">
                Nenhum integrante ainda.
                <br />
                Busque um cliente abaixo para adicionar.
              </div>
            ) : (
              <>
                {integrantes.map((c) => {
                  const removing = removingCodigo === c.codigo;
                  return (
                    <div key={`m:${c.id || c.codigo}`} className="uc-cg-row">
                      <span className="uc-cg-code">{c.codigo}</span>
                      <div className="uc-cg-razao">
                        <span className="uc-cg-razao-name" title={c.razaoSocial}>
                          {c.razaoSocial}
                        </span>
                        <span className="uc-cg-razao-doc">{c.cnpj}</span>
                      </div>
                      <span className="uc-cg-ctr-pill">{c.contratos}</span>
                      {onRemoveIntegrante ? (
                        <button
                          type="button"
                          className="uc-cg-remove"
                          onClick={() => onRemoveIntegrante(c.codigo)}
                          disabled={Boolean(removingCodigo)}
                          aria-label={`Remover ${c.razaoSocial}`}
                        >
                          {removing ? (
                            <Loader2 className="uc-cg-spin" size={14} strokeWidth={2} />
                          ) : (
                            <Trash size={14} strokeWidth={2} />
                          )}
                        </button>
                      ) : (
                        <span className="uc-cg-remove-spacer" />
                      )}
                    </div>
                  );
                })}
                {pendingIntegrantes.map((c) => (
                  <div key={`p:${c.id || c.codigo}`} className="uc-cg-row uc-cg-row--pending">
                    <span className="uc-cg-code">{c.codigo}</span>
                    <div className="uc-cg-razao">
                      <span className="uc-cg-razao-name" title={c.razaoSocial}>
                        {c.razaoSocial}
                        <span className="uc-cg-novo-badge">Novo</span>
                      </span>
                      <span className="uc-cg-razao-doc">{c.cnpj}</span>
                    </div>
                    <span className="uc-cg-ctr-pill">{c.contratos}</span>
                    <button
                      type="button"
                      className="uc-cg-remove"
                      onClick={() => onRemovePending?.(c.id || c.codigo)}
                      aria-label={`Remover ${c.razaoSocial} da seleção`}
                    >
                      <Trash size={14} strokeWidth={2} />
                    </button>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      </div>

      <div className="uc-cg-block uc-cg-add" ref={wrapRef}>
        <label className="uc-cg-label" htmlFor={`${nomePortalId}-search`}>
          Adicionar cliente
        </label>
        <div className="uc-cg-search">
          <Search className="uc-cg-search-icon" size={15} strokeWidth={2} aria-hidden />
          <input
            id={`${nomePortalId}-search`}
            className="uc-cg-search-input"
            value={query}
            onChange={(e) => {
              onQueryChange?.(e.target.value);
              setDropdownOpen(true);
            }}
            onFocus={() => setDropdownOpen(true)}
            onBlur={() => {
              window.setTimeout(() => {
                if (!wrapRef.current?.contains(document.activeElement)) {
                  setDropdownOpen(false);
                }
              }, 0);
            }}
            placeholder="Buscar por código, CNPJ ou razão social…"
            aria-label={searchAriaLabel}
            aria-expanded={showDropdown}
            aria-autocomplete="list"
            disabled={searchDisabled}
            autoComplete="off"
          />
        </div>
        {showDropdown && (
          <div className="uc-cg-dropdown" role="listbox">
            <div className="uc-cg-dropdown-scroll">
              {searchLoading ? (
                <div className="uc-cg-dropdown-msg">
                  <Loader2 className="uc-cg-spin" size={16} aria-hidden />
                  Buscando…
                </div>
              ) : tooShort ? (
                <div className="uc-cg-dropdown-msg">
                  Digite ao menos {searchMin} caracteres para buscar.
                </div>
              ) : q.length === 0 ? (
                <div className="uc-cg-dropdown-msg">Digite para buscar clientes.</div>
              ) : noResults ? (
                <div className="uc-cg-dropdown-msg">
                  Nenhum cliente encontrado para &ldquo;{q}&rdquo;.
                </div>
              ) : (
                searchResults.map((r) => {
                  const linked = Boolean(r.jaVinculado);
                  const sel = isSelected(r.id);
                  return (
                    <button
                      key={r.id}
                      type="button"
                      role="option"
                      aria-selected={sel}
                      aria-disabled={linked || undefined}
                      className={[
                        'uc-cg-dd-item',
                        sel ? 'is-selected' : '',
                        linked ? 'is-linked' : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      onClick={() => {
                        if (linked) return;
                        onToggleSelect?.(r);
                      }}
                      title={linked ? 'Cliente já cadastrado no portal' : undefined}
                    >
                      <span
                        className={['uc-cg-check', sel ? 'is-on' : ''].filter(Boolean).join(' ')}
                        aria-hidden
                      >
                        {sel ? (
                          <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                            <path
                              d="M1 4L3.5 6.5L9 1"
                              stroke="white"
                              strokeWidth="1.6"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        ) : null}
                      </span>
                      <span className="uc-cg-dd-code">{r.codigo}</span>
                      <div className="uc-cg-dd-meta">
                        <div className="uc-cg-dd-name-row">
                          <span className="uc-cg-dd-name">{r.razaoSocial}</span>
                          {linked && <span className="uc-cg-linked-badge">Já cadastrado</span>}
                        </div>
                        <span className="uc-cg-dd-doc">{r.cnpj}</span>
                      </div>
                      <span className="uc-cg-ctr-pill">{r.contratos}</span>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
